// Espelha `games/sudoku/domain/sudoku-shape.ts` do games-rec (spec D1/D3/D4).
// A "forma" de um sudoku: lado da grade e tamanho do bloco. Lista FECHADA (spec D1):
// 9×9 com blocos 3×3 e 15×15 com blocos de 5 colunas × 3 linhas. `boxW` é o
// `box-width` do blob (colunas por bloco) e `boxH` o `box-height` (linhas por bloco).

export interface SudokuShape {
  size: number;
  boxW: number;
  boxH: number;
}

export const SUDOKU_9: SudokuShape = { size: 9, boxW: 3, boxH: 3 };
export const SUDOKU_15: SudokuShape = { size: 15, boxW: 5, boxH: 3 };
export const SUPPORTED_SHAPES: readonly SudokuShape[] = [SUDOKU_9, SUDOKU_15];

export function findShape(
  width: number,
  height: number,
  boxW: number,
  boxH: number,
): SudokuShape | null {
  return (
    SUPPORTED_SHAPES.find(
      (s) => s.size === width && s.size === height && s.boxW === boxW && s.boxH === boxH,
    ) ?? null
  );
}

function valuesFor(shape: SudokuShape): string[] {
  return Array.from({ length: shape.size }, (_, i) => String(i + 1));
}

/** Alfabeto como o blob grava (spec D3): 9×9 colado, 15×15 separado por vírgula. */
export function alphabetFor(shape: SudokuShape): string {
  const values = valuesFor(shape);
  return shape.size <= 9 ? values.join('') : values.join(',');
}

/** Regra de leitura (spec D4): com vírgula → por vírgula; senão por caractere. */
export function parseAlphabet(alphabet: string | undefined, shape: SudokuShape): string[] {
  if (!alphabet) return valuesFor(shape);
  const parts = alphabet.includes(',') ? alphabet.split(',') : alphabet.split('');
  return parts.map((p) => p.trim()).filter((p) => p !== '');
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export function shapeFromGame(game: unknown): SudokuShape | null {
  let parsed = game;
  if (typeof game === 'string') {
    try { parsed = JSON.parse(game); } catch { return null; }
  }
  if (!isRecord(parsed)) return null;
  const cc = parsed['crossword-compiler'];
  const puzzle = isRecord(cc) ? cc['rectangular-puzzle'] : null;
  const sudoku = isRecord(puzzle) ? puzzle.sudoku : null;
  if (!isRecord(sudoku)) return null;
  const attrs = isRecord(sudoku.attributes) ? sudoku.attributes : {};
  const grid = isRecord(sudoku.grid) && isRecord(sudoku.grid.attributes) ? sudoku.grid.attributes : {};
  return findShape(Number(grid.width), Number(grid.height), Number(attrs['box-width']), Number(attrs['box-height']));
}

export function acceptsPartial(text: string, values: string[]): boolean {
  return text === '' || values.some((v) => v.startsWith(text));
}

export function isCompleteValue(text: string, values: string[]): boolean {
  return values.includes(text);
}
